import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { REDEEM_PASSWORD_RESET } from "../../utils/GraphQL/mutations";
import Auth from "../../utils/auth";

import "./login.css";
import BdrInput from "../../components/BdrInput/BdrInput";
import BdrButton from "../../components/BdrButton/BdrButton";

// Landing page for the password-reset link/code. The reset link the server
// emails is https://bigginsdoorrefinishing.com/reset?email=...&code=..., which
// iOS/Android route into the app when installed (Universal/App Links) and which
// otherwise lands here. The code is NOT consumed on open — only on submit — so
// an email-scanner prefetch of the link can't burn it.
const ResetPassword = ({ changeLoggedIn, displayNav, changeFooter }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [redeem, { loading }] = useMutation(REDEEM_PASSWORD_RESET);

  // Deep-link landing: suppress the marketing nav + footer while on /reset, and
  // restore on unmount. The footer renders a HIDDEN full-width /login-portal
  // click-hotspot (Footer.jsx) — on a reset deep-link landing a stray tap there
  // bounces the user off the reset task to the login page. (2026-06-26)
  useEffect(() => {
    displayNav?.(false);
    changeFooter?.(false);
    return () => {
      displayNav?.(true);
      changeFooter?.(true);
    };
  }, [displayNav, changeFooter]);

  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState(searchParams.get("code") || "");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPass.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPass !== confirmPass) {
      setError("Passwords do not match.");
      return;
    }
    try {
      const { data } = await redeem({
        variables: {
          email: email.trim().toLowerCase(),
          token: code.trim(),
          newPass,
        },
      });
      const token = data?.redeemPasswordReset?.token;
      if (!token) {
        setError("Invalid or expired reset code.");
        return;
      }
      await Auth.login(token);
      if (changeLoggedIn) await changeLoggedIn(true);
      navigate("/bdr-office/tasks");
    } catch (err) {
      // Generic — the server returns the same error for every failure mode.
      setError("Invalid or expired reset code.");
    }
  };

  return (
    <div className="login-box">
      <div className="login-card">
        <h2 className="login-title">Reset Password</h2>
        <form className="login-form" onSubmit={handleSubmit}>
          <BdrInput
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputClassName="bdr-input--autofill-dark"
          />
          <BdrInput
            label="Reset code"
            type="text"
            placeholder="From your email"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <BdrInput
            label="New password"
            type={showPass ? "text" : "password"}
            placeholder="At least 8 characters"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            autoComplete="new-password"
            inputClassName="bdr-input--autofill-dark login-password"
            trailing={
              <BdrButton
                variant="plain"
                size="sm"
                className="login-show-toggle"
                onClick={() => setShowPass((v) => !v)}
                ariaLabel={showPass ? "Hide password" : "Show password"}
                aria-pressed={showPass}
              >
                {showPass ? "Hide" : "Show"}
              </BdrButton>
            }
          />
          <BdrInput
            label="Confirm new password"
            type={showPass ? "text" : "password"}
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            autoComplete="new-password"
            inputClassName="bdr-input--autofill-dark"
          />
          {/* The form's only submit control — keep type="submit". */}
          <BdrButton
            type="submit"
            variant="confirm-solid"
            className="login-btn-full"
            disabled={loading}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </BdrButton>
        </form>
        {error && (
          <p className="bdr-alert bdr-alert--danger" role="alert">
            {error}
          </p>
        )}
        {/* A link, not a button: it goes somewhere (BdrButton renders a
            <button>, which would drop the href). Styled as the action button. */}
        <Link to="/login-portal" className="bdr-btn bdr-btn--action login-back-link">
          Back to login
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;
